import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import {
  HistoryIcon,
  ListChecksIcon,
  Undo2Icon,
  UploadIcon,
} from "lucide-react";

import { formatDateAt } from "simplydo/core/dates";
import type { OpenAPI } from "simplydo/interfaces";
import { styled } from "simplydo/react/styled";
import { UiColor } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";
import { Popup } from "simplydo/react/ui/popup";

type StatusHistoryItem = NonNullable<
  OpenAPI.GET<"/ideas/{id}">["response"]["statusHistory"]
>[number];

const EventContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 6px 0 0;
  max-height: 400px;
  min-width: 260px;
  overflow: auto;
  scrollbar-width: thin;
`;

const EventRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  margin-bottom: 4px;
  padding-bottom: 4px;
  border-bottom: 1px solid #f1f1f1;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }

  .icon-container {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #f3f4f6;
    flex-shrink: 0;
  }

  .info-container {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .meta {
    font-size: 0.8rem;
    color: rgba(0, 0, 0, 0.45);
  }
`;

const actionPresentation = {
  ideaSubmitted: {
    icon: UploadIcon,
    labelKey: "ideas.status.history.submitted",
  },
  ideaUnsubmitted: {
    icon: Undo2Icon,
    labelKey: "ideas.status.history.unsubmitted",
  },
  publicStatusChanged: {
    icon: ListChecksIcon,
    labelKey: "ideas.status.history.statusUpdated",
  },
} as const;

type IdeaStatusHistoryButtonProps = {
  statusHistory?: StatusHistoryItem[] | null;
};

export function IdeaStatusHistoryButton({
  statusHistory,
}: IdeaStatusHistoryButtonProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const items = useMemo(
    () =>
      (statusHistory ?? []).filter(
        (
          item,
        ): item is StatusHistoryItem & {
          action: keyof typeof actionPresentation;
        } =>
          Boolean(
            item?.action && item.action in actionPresentation && item.createdAt,
          ),
      ),
    [statusHistory],
  );

  if (!items.length) {
    return null;
  }

  return (
    <Popup
      open={open}
      onClose={() => setOpen(false)}
      on="click"
      onOpen={() => setOpen(true)}
      position="bottom right"
      trigger={
        <Button size="small" variant="outline">
          <Icon icon={HistoryIcon} />
          {t("ideas.status.history.button")}
        </Button>
      }
    >
      {
        <EventContainer>
          {items.map((item, index) => {
            const presentation = actionPresentation[item.action];
            return (
              <EventRow key={`${item.action}-${item.createdAt}-${index}`}>
                <div className="icon-container">
                  <Icon icon={presentation.icon} />
                </div>
                <div className="info-container">
                  <span className="meta">{formatDateAt(item.createdAt)}</span>
                  <span>
                    {t(presentation.labelKey)}

                    {item.ownerNewStatus ? (
                      <>
                        {": "}
                        <Label
                          size="small"
                          color={item.ownerNewStatus.color as UiColor}
                        >
                          {item.ownerNewStatus.name}
                        </Label>
                      </>
                    ) : null}
                  </span>
                </div>
              </EventRow>
            );
          })}
        </EventContainer>
      }
    </Popup>
  );
}
